import { Flex, Spinner, Text } from '@smarterservices/smarter-ui';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Loader from '../../components/Common/Loader';
import SecondCameraCodePrompt from '../../components/SecondCamera/Enrollment/CodePrompt';
import SecondCameraCamera from '../../components/SecondCamera/Enrollment/Camera';
import useAppStore from '../../stores/app';
import useSecondCameraStore from '../../stores/secondCamera';
import PageError from '../../components/PageError';
import SecondCameraSessionEnded from '../../components/SecondCamera/Device/SessionEnded';
import { useLogger } from '@ui/shared/components/Logger';

/**
 * SecondCamera Route Component - Main entry point for second camera functionality
 * 
 * This component manages the complete second camera flow for proctoring sessions:
 * - Handles initialization from URL codes or stored parameters
 * - Manages wake lock to prevent device sleep during sessions
 * - Renders appropriate UI states based on initialization and session status
 * - Provides error handling and session end detection
 * 
 * The component follows this flow:
 * 1. Initialize from URL code or stored params
 * 2. Show code prompt if no initialization data
 * 3. Show camera interface once initialized
 * 4. Handle session end and error states
 * 
 * @param {Object} props - Component props
 * @param {boolean} [props.isReconnect=false] - Whether this is a reconnection attempt
 * @param {boolean} [props.inSession=false] - Whether currently in an active session
 * @returns {JSX.Element} The rendered component based on current state
 */
const SecondCamera = ({ isReconnect = false, inSession = false }) => {
  // Local component state for managing UI flow and loading states
  
  /** @type {[Object|null, Function]} Current step data (legacy, currently unused) */
  const [step, setStep] = useState(null);
  
  /** @type {[boolean, Function]} General loading state for the component */
  const [loading, setLoading] = useState(true);
  
  /** @type {[boolean, Function]} Whether the current task/step is complete */
  const [isTaskComplete, setIsTaskComplete] = useState(false);
  
  /** @type {[boolean, Function]} Whether valid setup has been checked (legacy, unused) */
  const [checkedForValidSetup, setCheckedForValidSetup] = useState(false);
  
  /** @type {[boolean, Function]} Whether the component has been initialized (legacy, unused) */
  const [isInitialized, setIsInitialized] = useState(false);
  
  /** @type {[boolean, Function]} Whether currently checking/validating the session code */
  const [checkingCode, setCheckingCode] = useState(true);
  
  /** @type {React.MutableRefObject<boolean>} Whether an intro step is needed for reconnection */
  const needsIntroStep = useRef(isReconnect);
  
  // Navigation and routing
  const navigate = useNavigate();
  
  // Global app state selectors
  /** @type {string|null} Current page error text to display */
  const pageErrorText = useAppStore((state) => state.pageErrorText);
  
  /** @type {string|null} Current error code for debugging */
  const errorCode = useAppStore((state) => state.errorCode);
  
  // Step metadata management (legacy functionality)
  /** @type {React.MutableRefObject<Object>} Reference to store step metadata */
  const stepMetadataRef = useRef({});
  
  /**
   * Update step metadata (legacy functionality)
   * @param {Object} metadata - Metadata to store for the current step
   */
  const setStepMetadata = (metadata) => {
    stepMetadataRef.current = metadata;
  };
  
  // Logging setup
  const logger = useLogger();
  const logContext = 'SecondCamera';

  // App store methods and state
  const {
    /** @type {Function} Initialize app from stored session data or URL code */
    initializeAppFromStash,
  } = useAppStore();
  
  /** @type {string} Current application phase (enrollment, session, etc.) */
  const phase = useAppStore((state) => state.phase);
  
  /** @type {boolean} Whether the main app has been initialized with session data */
  const initialized = useAppStore((state) => state.initialized);
  
  /** @type {boolean} Whether the proctoring session has ended */
  const hasSessionEnded = useSecondCameraStore((state) => state.hasSessionEnded);
  

  // Component initialization effect - runs once on mount
  useEffect(() => {

    /**
     * Initialize the second camera application
     * 
     * This function handles the complete initialization flow:
     * 1. Checks for existing stored session parameters
     * 2. Extracts and validates session code from URL
     * 3. Requests wake lock to prevent device sleep
     * 4. Initializes app from code or shows code prompt
     */
    const initializeSecondCameraApp = async () => {
      // Check if there are already stored session parameters from previous initialization
      const storedParams = useAppStore.getState().getStoredParams();
      
      // Extract session code from URL query parameters
      let code = new URLSearchParams(window.location.search).get('code');
      
      // Normalize the code: remove dashes and convert to uppercase for consistency
      if(code){
        code = code.replace(/-/g, '').toUpperCase();
      }

      // Request wake lock to prevent mobile device from sleeping during proctoring
      // This is critical for maintaining camera feed and connection
      try {
        const wakeLock = await navigator.wakeLock.request('screen');

        // Log when wake lock is released (device sleep, user action, etc.)
        wakeLock.addEventListener('release', () => {
          logger.logInfo(logContext, 'Wake lock released');
        });
      } catch (error) {
        // Wake lock may fail on some browsers/devices - log but continue
        logger.logError(logContext, 'Failed to acquire wake lock', error);
      }

      // Determine initialization path based on available data
      if(!storedParams && !code){
        // No stored params and no URL code - show code input prompt
        setCheckingCode(false);
        setIsInitialized(false);
      }else if(code){
        // URL code provided - initialize from code
        setCheckingCode(true);
        
        // Initialize app with the session code and specify second camera type
        await initializeAppFromStash({code, type: 'second-camera'});
        setCheckingCode(false);
      }else{
        // Stored params exist - app should already be initialized
        setCheckingCode(false);
      }
    }

    // Execute initialization on component mount
    initializeSecondCameraApp();

  }, []);

  // Render logic - returns appropriate component based on current state
  
  // Error state: Show error page if there's a page-level error
  if(pageErrorText){
    return ( 
      <PageError 
        errorText={pageErrorText} 
        showRefreshButton={false} 
        errorCode={errorCode} 
      /> 
    );
  }
  
  // Loading state: Show loader while checking/validating session code
  if(checkingCode){
    return (
      <Loader text="Initializing..." />
    )
  }

  // Session ended state: Show session ended component
  if(hasSessionEnded){
    return (
      <SecondCameraSessionEnded />
    )
  }

  // Initialization state: Show appropriate component based on app initialization
  if(!initialized){
    // App not initialized - show code prompt for manual code entry
    return (
      <SecondCameraCodePrompt
        stepData={step}
        setIsTaskComplete={setIsTaskComplete}
      /> 
    )
  }else{
    // App initialized - show camera interface for second camera functionality
    return (
      <SecondCameraCamera
        stepData={step}
        setIsTaskComplete={setIsTaskComplete}
      /> 
    )
  }



  
};

export default SecondCamera;
